/**
 * Connectivity beyond navigator.onLine — useful on mountain trails where
 * the OS reports "online" but requests time out or fail intermittently.
 */

let reachable = navigator.onLine;
let lastProbeAt = 0;
let probing = null;

const PROBE_COOLDOWN_MS = 8000;
const PROBE_TIMEOUT_MS = 3500;

/** Lightweight same-origin probe — works offline via SW after first load. */
async function runProbe() {
  if (!navigator.onLine) {
    reachable = false;
    lastProbeAt = Date.now();
    return false;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch('/pwa-icon.svg', {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
    });
    reachable = res.ok;
  } catch {
    reachable = false;
  } finally {
    clearTimeout(timer);
    lastProbeAt = Date.now();
  }
  return reachable;
}

/** Synchronous best-guess — prefer store.js isOnline() for gating writes. */
export function isReachable() {
  return navigator.onLine && reachable;
}

export function markNetworkFailure() {
  reachable = false;
  lastProbeAt = Date.now();
}

export function markNetworkSuccess() {
  reachable = true;
  lastProbeAt = Date.now();
}

/**
 * Active connectivity check with cooldown. Use before large downloads.
 * @param {{ force?: boolean }} [opts]
 */
export async function checkConnectivity({ force = false } = {}) {
  if (!navigator.onLine) {
    reachable = false;
    return false;
  }
  if (!force && Date.now() - lastProbeAt < PROBE_COOLDOWN_MS) {
    return reachable;
  }
  if (probing) return probing;
  probing = runProbe().finally(() => {
    probing = null;
  });
  return probing;
}

const listeners = new Set();

export function onConnectivityChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyListeners() {
  for (const fn of listeners) fn(isReachable());
}

/** Call once at app startup. */
export function initConnectivityMonitoring() {
  const handleOffline = () => {
    reachable = false;
    lastProbeAt = Date.now();
    notifyListeners();
  };

  const handleOnline = () => {
    checkConnectivity({ force: true }).then(() => notifyListeners());
  };

  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      checkConnectivity({ force: true }).then(() => notifyListeners());
    }
  });

  if (navigator.onLine) {
    checkConnectivity({ force: true }).catch(() => {});
  }
}
